"use client";

import React from "react";
import { Star } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const testimonials = [{
  id: 1,
  name: "Celine Da Silva",
  company: "Homeowner",
  text: "\"Nick and his team were an absolute dream to work with. We barely used our garden before — now we want to be out there all the time.\"",
  rating: 5,
  avatarSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1653984/img/testimonial_avatar_1.png",
  avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1653984/img/testimonial_avatar_1.png-wvc-srcset",
  avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1653984/img/testimonial_avatar_1.png-wvc-sizes",
  initials: "SJ"
}, {
  id: 2,
  name: "Bruce Robinson",
  company: "Chen Properties",
  text: "\"Nick and his guys are completely customer-focused and very can-do. Creative, reliable, punctual, transparent — they transformed my garden from a building site into something I adore.\"",
  rating: 5,
  avatarSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1653984/img/testimonial_avatar_2.png",
  avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1653984/img/testimonial_avatar_2.png-wvc-srcset",
  avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1653984/img/testimonial_avatar_2.png-wvc-sizes",
  initials: "DC"
}, {
  id: 3,
  name: "Marc Rimmer",
  company: "TechCorp Inc.",
  text: "\"We've used Nick twice now, most recently for a complete garden renovation. Friendly, reliable, and creative — I would not hesitate to recommend him to anyone in SE London.\"",
  rating: 5,
  avatarSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1653984/img/testimonial_avatar_3.png",
  avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1653984/img/testimonial_avatar_3.png-wvc-srcset",
  avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1653984/img/testimonial_avatar_3.png-wvc-sizes",
  initials: "ER"
}, {
  id: 4,
  name: "Richard Norman",
  company: "Resident",
  text: "\"Nick did a fantastic job reinvigorating our tired driveway. He listened, offered great suggestions, and the team were polite and incredibly hard working. I genuinely cannot recommend him highly enough.\"",
  rating: 5,
  avatarSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1653984/img/testimonial_avatar_4.png",
  avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1653984/img/testimonial_avatar_4.png-wvc-srcset",
  avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1653984/img/testimonial_avatar_4.png-wvc-sizes",
  initials: "MC"
}, {
  id: 5,
  name: "Dan H",
  company: "Smith & Co.",
  text: "\"Nick and his team utterly transformed our garden — with gusto, on time, and on budget. Brilliant aftercare too. Would strongly recommend.\"",
  rating: 5,
  avatarSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1653984/img/testimonial_avatar_5.png",
  avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1653984/img/testimonial_avatar_5.png-wvc-srcset",
  avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1653984/img/testimonial_avatar_5.png-wvc-sizes",
  initials: "JS"
}, {
  id: 6,
  name: "Robert Taylor",
  company: "Taylor Estates",
  text: "\"Nick's team landscaped our whole back garden — paving, retaining wall, fencing, decking, and a pergola. Great communication and craftsmanship throughout.\"",
  rating: 5,
  avatarSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1653984/img/testimonial_avatar_6.png",
  avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1653984/img/testimonial_avatar_6.png-wvc-srcset",
  avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1653984/img/testimonial_avatar_6.png-wvc-sizes",
  initials: "RT"
}];
interface AnimatedCardProps {
  testimonial: (typeof testimonials)[0];
  index: number;
  "data-index"?: number;
}
function AnimatedCard({
  testimonial,
  index,
  "data-index": dataIndex
}: AnimatedCardProps) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <div ref={ref} data-index={dataIndex} className={cn("transition-all duration-700 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")} style={{
    transitionDelay: `${index * 100}ms`
  }}>
      <Card className="h-full flex flex-col shadow-sm hover:shadow-md transition-shadow duration-300 border-border/50 bg-card/50 backdrop-blur-sm">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-4">
            
            <div className="flex flex-col">
              <h3 className="font-default font-semibold text-foreground leading-none mb-1.5">
                {testimonial.name}
              </h3>
              
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-grow flex flex-col gap-4">
          <div className="flex gap-1">
            {Array.from({
            length: 5
          }).map((_, i) => <Star key={i} className={cn("size-4", i < testimonial.rating ? "fill-yellow-400 text-yellow-400" : "fill-muted text-muted")} />)}
          </div>
          <p className="text-muted-foreground leading-relaxed flex-grow">
            "{testimonial.text}"
          </p>
        </CardContent>
      </Card>
    </div>;
}
export default function CustomerTestimonials() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="customertestimonials" className="bg-background py-16 md:py-24 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={headerRef} className={cn("max-w-3xl mx-auto text-center mb-12 md:mb-16 transition-all duration-700 ease-out", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4">
            What Our Clients Say
          </h2>
          <p className="text-lg text-muted-foreground">
            Don't just take our word for it. Read how Nick Landscapes has transformed outdoor spaces for homeowners and businesses alike.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {testimonials.map((testimonial, i) => <AnimatedCard key={testimonial.id} testimonial={testimonial} index={i} data-index={i} />)}
        </div>
      </div>
    </section>;
}